import {WishForm} from "@/components/Wish/WishForm.tsx";
import {AuthProfile, Wish, Wishlist} from "@/api/types.ts";
import {useOutletContext, useParams} from "react-router-dom";
import {useWishlist} from "@/providers/WishlistProvider.tsx";
import {Caption, Headline} from "@telegram-apps/telegram-ui";
import {useNavigation} from "@/providers/NavigationProvider.tsx";
import {useTranslation} from "react-i18next";
import {useAuthProfile} from "@/providers/AuthProfileProvider.tsx";
import {WishOutletContext} from "@/pages/layout/WishOutletContext.ts";

export const EditWishPage = ()=> {
  const { wishId, wishlistId } = useParams();
  const { navigateBack } = useNavigation();
  const { t } = useTranslation();

  let onWishSaved: (() => Promise<void>) | undefined = undefined;
  let wishFromContext: Wish | undefined = undefined;
  let setWishFromContext: ((wish: Wish | undefined) => void) | undefined = undefined;
  try {
    const outletContext = useOutletContext<WishOutletContext>();
    onWishSaved = outletContext.onWishSaved;
    wishFromContext = outletContext.wish;
    setWishFromContext = outletContext.setWish;
  } catch {
    onWishSaved = undefined;
    wishFromContext = undefined;
    setWishFromContext = undefined;
  }

  let wishlistFromContext: Wishlist | undefined = undefined;
  let setWishlistFromContext: (wishlist: Wishlist | undefined) => void = () => {};
  try {
    const { wishlist, setWishlist } = useWishlist();
    wishlistFromContext = wishlist;
    setWishlistFromContext = setWishlist
  } catch {
    setWishlistFromContext(undefined);
  }

  let profileFromContext: AuthProfile | undefined = undefined;
  try {
    const { authProfile } = useAuthProfile();
    profileFromContext = authProfile;
  } catch {
    profileFromContext = undefined;
  }

  let wish: Wish | undefined = wishFromContext;
  if (wishId) {
    wish = wish || wishlistFromContext?.wishes.find(wish => wish.id === wishId);

    if (!wish && profileFromContext) {
      wish = profileFromContext.allWishes.find(wish => wish.id === wishId);
    }
  }

  const onSaved = async (wish: Wish) => {
    if (wishlistFromContext) {
      const existingWishIndex = wishlistFromContext.wishes.findIndex(w => w.id === wish.id);
      if (existingWishIndex !== -1) {
        const wishes = [...wishlistFromContext.wishes];
        wishes[existingWishIndex] = wish;
        setWishlistFromContext({...wishlistFromContext, wishes});
      } else {
        const wishes = [wish, ...wishlistFromContext.wishes];
        setWishlistFromContext({...wishlistFromContext, wishes});
      }
    }

    if (onWishSaved) {
      await onWishSaved();
    }
    setWishFromContext?.(wish);

    navigateBack(true);
  }

  const onDeleted = async (wishId: string) => {
    if (wishlistFromContext) {
      const wishes = wishlistFromContext.wishes.filter(w => w.id !== wishId);
      setWishlistFromContext({...wishlistFromContext, wishes});
    }

    if (onWishSaved) {
      await onWishSaved();
    }
    setWishFromContext?.(undefined);

    navigateBack(true);
  }

  const pageClassName = `mt-3 pb-10 ${wishlistId ? "" : "mx-3"}`;

  if (wishId && !wish) {
    return <Caption className="text-color-hint">{t("wish.gifted.notLoaded")}</Caption>;
  }

  return (
    <div className={pageClassName}>
      <Headline className={"text-center mb-3"} weight={"2"}>{wish ? t('wish.action.edit') : t('wish.action.add')}</Headline>
      <WishForm wish={wish}
                wishlists={profileFromContext?.wishlistPreviews}
                onSaved={onSaved}
                onDeleted={onDeleted}
                defaultWishlistIds={wishlistFromContext?.id ? [wishlistFromContext?.id] : []}/>
    </div>
  );
}
